import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import { Button, ShimmerDiv } from 'fluxo-ui';
import { BarChartIcon, CalendarIcon, ListIcon, PlusIcon, QuestionCircle, UserGroupIcon } from 'fluxo-ui/icons';

import { Card, EmptyState, PageHeader, StatCard } from '../common';
import analyticsService from '../../services/analytics.service';
import examService from '../../services/exam.service';
import userService from '../../services/user.service';
import { useAuthStore } from '../../store/authStore';

import type { StatTone } from '../common/StatCard';

interface OrgStats {
    totalMembers: number;
    totalExams: number;
    totalQuestions: number;
    avgScore: number;
    activeExams: number;
    completedAttempts: number;
    pendingGrading: number;
}

interface ActivityItem {
    id: string;
    text: string;
    when: string;
    kind: 'exam' | 'user';
    action: () => void;
}

const emptyStats: OrgStats = {
    totalMembers: 0,
    totalExams: 0,
    totalQuestions: 0,
    avgScore: 0,
    activeExams: 0,
    completedAttempts: 0,
    pendingGrading: 0,
};

const quickActionChipClasses: Record<StatTone, string> = {
    blue: 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    violet: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    sky: 'bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400',
    gray: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const OrgAdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [stats, setStats] = useState<OrgStats | null>(null);
    const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const [data, examsResponse, usersResponse] = await Promise.all([
                    analyticsService.getOrgAdminStats().catch(() => null),
                    examService.listExams({ limit: 5, sortBy: 'createdAt', sortOrder: 'desc' }).catch(() => ({ exams: [] }) as any),
                    userService.listUsers({ limit: 5, sortBy: 'createdAt', sortOrder: 'desc' }).catch(() => ({ users: [] }) as any),
                ]);
                setStats(data || emptyStats);

                const activity = [
                    ...(examsResponse?.exams || []).map((e: any) => ({
                        id: `exam-${e._id}`,
                        text: `Exam "${e.title}" (${e.status})`,
                        when: e.createdAt,
                        kind: 'exam' as const,
                        action: () => navigate(`/exams/${e._id}/edit`),
                    })),
                    ...((usersResponse?.users || []) as any[]).map((u: any) => ({
                        id: `user-${u._id}`,
                        text: `${u.profile?.name || u.name || u.email} joined as ${(u.role || '').replace(/_/g, ' ')}`,
                        when: u.createdAt,
                        kind: 'user' as const,
                        action: () => navigate('/users'),
                    })),
                ]
                    .filter((a) => a.when)
                    .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())
                    .slice(0, 6);
                setRecentActivity(activity);
            } catch (error) {
                console.error('Failed to fetch org admin dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const quickActions: { label: string; description: string; icon: React.ComponentType<{ className?: string }>; action: () => void; tone: StatTone }[] = [
        { label: 'Create New Exam', description: 'Build a new assessment', icon: PlusIcon, action: () => navigate('/exams/create'), tone: 'blue' },
        { label: 'Add Questions', description: 'Expand question bank', icon: QuestionCircle, action: () => navigate('/questions/create'), tone: 'emerald' },
        { label: 'Invite Members', description: 'Add team members', icon: UserGroupIcon, action: () => navigate('/organization'), tone: 'violet' },
        { label: 'View Analytics', description: 'Performance insights', icon: BarChartIcon, action: () => navigate('/analytics'), tone: 'amber' },
    ];

    if (loading) {
        return (
            <div className="space-y-8">
                <ShimmerDiv level={2} className="h-20 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <ShimmerDiv key={i} level={3} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    const userName = user?.profile?.name || user?.name || user?.email || '';

    return (
        <div className="space-y-6">
            <PageHeader
                title={userName ? `Welcome back, ${userName}!` : 'Welcome back!'}
                description="Here's what's happening in your organization"
                className="mb-0"
            />

            {(stats?.pendingGrading ?? 0) > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {stats?.pendingGrading} submissions pending grading
                    </span>
                    <Button label="View" variant="danger" size="sm" layout="outlined" onClick={() => navigate('/exams')} />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                <StatCard
                    label="Team Members"
                    value={stats?.totalMembers ?? 0}
                    icon={UserGroupIcon}
                    tone="blue"
                    onClick={() => navigate('/users')}
                />
                <StatCard
                    label="Exams"
                    value={stats?.totalExams ?? 0}
                    icon={CalendarIcon}
                    tone="emerald"
                    hint={`${stats?.activeExams ?? 0} active`}
                    onClick={() => navigate('/exams')}
                />
                <StatCard
                    label="Questions"
                    value={stats?.totalQuestions ?? 0}
                    icon={QuestionCircle}
                    tone="violet"
                    onClick={() => navigate('/questions')}
                />
                <StatCard
                    label="Avg. Score"
                    value={`${stats?.avgScore ?? 0}%`}
                    icon={BarChartIcon}
                    tone="amber"
                    hint={`${stats?.completedAttempts ?? 0} attempts`}
                    onClick={() => navigate('/analytics')}
                />
            </div>

            <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {quickActions.map((action) => {
                        const Icon = action.icon;
                        return (
                            <button
                                key={action.label}
                                onClick={action.action}
                                className="group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-teal-300 dark:hover:border-teal-700 hover:shadow-sm transition-all p-5 text-left"
                            >
                                <div
                                    className={classNames(
                                        'w-10 h-10 rounded-lg flex items-center justify-center mb-3',
                                        quickActionChipClasses[action.tone],
                                    )}
                                >
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                                    {action.label}
                                </div>
                                <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{action.description}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            <Card title="Recent Activity" padding="none">
                {recentActivity.length === 0 ? (
                    <EmptyState
                        icon={ListIcon}
                        title="No recent activity"
                        description="Activity from exams and members will appear here"
                        size="sm"
                    />
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {recentActivity.map((item) => (
                            <button
                                key={item.id}
                                onClick={item.action}
                                className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                            >
                                <span
                                    className={classNames('w-2 h-2 rounded-full shrink-0', {
                                        'bg-teal-500': item.kind === 'exam',
                                        'bg-cyan-500': item.kind === 'user',
                                    })}
                                    aria-hidden
                                />
                                <span className="text-sm text-slate-900 dark:text-white truncate flex-1">
                                    {item.text}
                                    <span className="ml-2 text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide">
                                        {item.kind}
                                    </span>
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0 tabular-nums">
                                    {new Date(item.when).toLocaleDateString()}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default OrgAdminDashboard;
